import { AppDispatch } from "../../store/store";
import {
  AppTypeWithAuthType,
  clearAuthError,
  getAccessToken,
  setAuthIssuer,
  setAuthReferrer,
} from "../../store/slices/authSlice";
import { imsIdsConfig } from "../../Config/idsConfig";
import { AuthType } from "../../types/authType";
import {
  getCodeParams,
  handleAuthError,
  getIDSIssuer,
  getReferrerUrl,
  validateTokenAndFetchUserInfo,
} from "./AuthHelper";

interface AuthenticationProps {
  queryParams: URLSearchParams;
  auth: AppTypeWithAuthType;
  dispatch: AppDispatch;
}

/**
 * Handles the authentication process by extracting query parameters, validating tokens,
 * and dispatching actions accordingly.
 * @param {Object} props - The props containing query parameters, authentication state, and dispatch function.
 * @param {URLSearchParams} props.queryParams - The URL query parameters.
 * @param {AppTypeWithAuthType} props.auth - The authentication state.
 * @param {AppDispatch} props.dispatch - The dispatch function from Redux.
 * @returns {void}
 */
export const handleAuthentication = async (props: AuthenticationProps) => {
  const { queryParams, auth, dispatch } = props;

  // Extract query parameters from URL
  const { code, codeVerifier } = getCodeParams(queryParams);

  let referrerUrl = "";

  // Get issuer and set referrer URL
  if (code) {
    const idsIssuer = getIDSIssuer(code, dispatch);
    if (idsIssuer) {
      referrerUrl = getReferrerUrl(idsIssuer);
      dispatch(setAuthReferrer(referrerUrl)); //set referrer in local storage
    }
  }

  // Handle new authentication request
  if (code && codeVerifier && !auth.authType.isAuthenticated) {
    const idsIssuer = imsIdsConfig.issuer; // Use the predefined idsConfig issuer

    // Save issuer details in state and localStorage
    dispatch(setAuthIssuer(idsIssuer as string));

    const requestBody = {
      ...imsIdsConfig,
      code,
      code_verifier: codeVerifier,
    };

    try {
      const actionResult = await dispatch(getAccessToken(requestBody));
      const response = actionResult.payload as AuthType;

      // Validate access token and get user info
      if (response.access_token) {
        await validateTokenAndFetchUserInfo(dispatch, referrerUrl);
      } else {
        handleAuthError(dispatch, null, referrerUrl);
      }
    } catch (error: any) {
      handleAuthError(dispatch, error, referrerUrl);
    } finally {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  } else {
    try {
      // Handle existing authentication session (e.g., page refresh)
      const storedAccessToken = localStorage.getItem("access_token") || "";
      if (storedAccessToken && !auth.authType.isAuthenticated) {
        // Clear any error messages
        dispatch(clearAuthError());

        // Validate accessToken and get userId from IMS identity server
        await validateTokenAndFetchUserInfo(dispatch, referrerUrl);
      } else {
        const referrerURL = localStorage.getItem("referrerURL") || "";

        // Unauthorized error message if no valid token is found
        handleAuthError(dispatch, null, referrerURL as string);
      }
    } catch (error: any) {
      handleAuthError(dispatch, error, referrerUrl);
    } finally {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }
};
